import exifr from 'exifr';
import { verificationResultSchema, type Assertion, type Tool, type VerificationResult } from '@upl/evidence-schema';
import {
  METADATA_ADAPTER_DESCRIPTOR,
  createAdapterExecutionResult,
  createEmptyEvidencePatch,
  type AdapterExecutionResult,
  type EvidenceAdapter,
  type EvidenceAdapterContext,
  type EvidencePatch,
} from '@upl/evidence-adapter-runtime';
import { stableEvidenceId, type ArtifactInput } from '@upl/provenance-core';

export interface MetadataField {
  key: string;
  label: string;
  value: string | number | boolean;
  family: 'EXIF' | 'XMP' | 'IPTC' | 'IMAGE';
}

export interface MetadataInspection {
  fields: MetadataField[];
  error: boolean;
}

const SAFE_FIELDS: Readonly<Record<string, { label: string; family: MetadataField['family'] }>> = {
  Make: { label: 'Camera maker', family: 'EXIF' },
  Model: { label: 'Camera model', family: 'EXIF' },
  LensModel: { label: 'Lens model', family: 'EXIF' },
  Software: { label: 'Software', family: 'EXIF' },
  Artist: { label: 'Artist', family: 'EXIF' },
  Copyright: { label: 'Copyright', family: 'EXIF' },
  DateTimeOriginal: { label: 'Original capture time', family: 'EXIF' },
  CreateDate: { label: 'Creation time', family: 'XMP' },
  ModifyDate: { label: 'Modification time', family: 'XMP' },
  MetadataDate: { label: 'Metadata time', family: 'XMP' },
  CreatorTool: { label: 'Creator tool', family: 'XMP' },
  DigitalSourceType: { label: 'Digital source type', family: 'XMP' },
  Description: { label: 'Description', family: 'XMP' },
  Headline: { label: 'Headline', family: 'IPTC' },
  Credit: { label: 'Credit', family: 'IPTC' },
  Byline: { label: 'Byline', family: 'IPTC' },
  ImageWidth: { label: 'Image width', family: 'IMAGE' },
  ImageHeight: { label: 'Image height', family: 'IMAGE' },
  ExifImageWidth: { label: 'EXIF image width', family: 'EXIF' },
  ExifImageHeight: { label: 'EXIF image height', family: 'EXIF' },
};

export async function inspectMetadata(input: ArtifactInput): Promise<MetadataInspection> {
  try {
    const parsed: unknown = await exifr.parse(input.bytes, {
      tiff: true,
      exif: true,
      xmp: true,
      iptc: true,
      gps: false,
      makerNote: false,
      userComment: false,
      translateKeys: true,
      translateValues: true,
      reviveValues: false,
      sanitize: true,
      mergeOutput: true,
      chunkLimit: 24,
    });
    if (!isRecord(parsed)) return { fields: [], error: false };

    const fields: MetadataField[] = [];
    for (const [key, definition] of Object.entries(SAFE_FIELDS)) {
      const value = primitiveValue(parsed[key]);
      if (value === undefined) continue;
      fields.push({ key, label: definition.label, value, family: definition.family });
    }
    return { fields: fields.slice(0, 32), error: false };
  } catch {
    return { fields: [], error: true };
  }
}

export class MetadataEvidenceAdapter implements EvidenceAdapter {
  readonly descriptor = METADATA_ADAPTER_DESCRIPTOR;

  supports(context: EvidenceAdapterContext): boolean {
    return context.artifact.detectedMediaType === 'image/jpeg' || context.artifact.detectedMediaType === 'image/png';
  }

  async inspect(context: EvidenceAdapterContext): Promise<AdapterExecutionResult> {
    const selectedAt = new Date().toISOString();
    const started = performance.now();
    const inspection = await inspectMetadata(context.artifact);
    const completedAt = new Date().toISOString();
    if (inspection.error) {
      return createAdapterExecutionResult({
        descriptor: this.descriptor,
        state: 'FAILED',
        selectedAt,
        startedAt: selectedAt,
        completedAt,
        durationMs: performance.now() - started,
        method: 'exifr',
        warnings: [{
          id: 'warning:metadata-parser-failure',
          code: 'METADATA_PARSER_FAILURE',
          message_key: 'warnings.metadata_parser_failure',
          evidence_refs: [],
        }],
        errors: [{ code: 'METADATA_PARSER_FAILURE' }],
        processingMetadata: { display_name: 'MetadataAdapter', display_version: 'exifr@7.1.3' },
      });
    }
    const patch = await metadataInspectionToPatch(context.artifact, inspection, completedAt);
    return createAdapterExecutionResult({
      descriptor: this.descriptor,
      state: 'COMPLETED',
      selectedAt,
      startedAt: selectedAt,
      completedAt,
      durationMs: performance.now() - started,
      method: 'exifr',
      evidence: patch,
      sourceMetadata: {
        supported_fields_present: inspection.fields.length > 0,
        gps_read: false,
        fields: Object.fromEntries(inspection.fields.map(({ key, value }) => [key, value])),
      },
      processingMetadata: { display_name: 'MetadataAdapter', display_version: 'exifr@7.1.3' },
    });
  }
}

export async function metadataInspectionToPatch(
  input: ArtifactInput,
  inspection: MetadataInspection,
  retrievedAt: string,
): Promise<EvidencePatch> {
  const patch = createEmptyEvidencePatch(METADATA_ADAPTER_DESCRIPTOR, retrievedAt, 'exifr');
  if (inspection.error || inspection.fields.length === 0) return patch;
  const sha256 = await sha256Hex(input.bytes);
  const artifactId = stableEvidenceId('artifact', sha256);
  const signalId = stableEvidenceId('signal', `${sha256}:metadata:present`);
  const provenanceBearingCount = inspection.fields.filter(({ family }) => family !== 'IMAGE').length;
  const source = { adapter: 'metadata', adapter_version: METADATA_ADAPTER_DESCRIPTOR.version, provider: 'Embedded image metadata' };
  patch.signals.push({
    id: signalId,
    source,
    evidence_refs: [],
    kind: 'EMBEDDED_METADATA',
    state: 'PRESENT',
    label: `${inspection.fields.length} supported metadata field${inspection.fields.length === 1 ? '' : 's'} found`,
    evidence_method: 'EMBEDDED_METADATA',
    evidence_strength: 'DECLARED',
    details: {
      families: [...new Set(inspection.fields.map(({ family }) => family))].sort(),
      cryptographically_verified: false,
      provenance_bearing_count: provenanceBearingCount,
    },
  });
  patch.edges.push({
    id: stableEvidenceId('edge', `${signalId}:${artifactId}`), source,
    from: signalId, to: artifactId, relation: 'DESCRIBES', evidence_refs: [],
  });
  for (const [index, field] of inspection.fields.entries()) {
    const assertionId = stableEvidenceId('assertion', `${sha256}:metadata:${field.key}:${index}`);
    const fieldSource = { ...source, provider: field.family, native_id: field.key };
    patch.assertions.push({
      id: assertionId, source: fieldSource, evidence_refs: [signalId], label: field.label, value: field.value,
    });
    patch.edges.push({
      id: stableEvidenceId('edge', `${signalId}:CONTAINS:${assertionId}`), source,
      from: signalId, to: assertionId, relation: 'CONTAINS', evidence_refs: [],
    });
    if (field.key === 'Software' || field.key === 'CreatorTool') {
      const toolId = stableEvidenceId('tool', `${sha256}:metadata-tool:${index}:${String(field.value)}`);
      patch.tools.push({ id: toolId, source: fieldSource, evidence_refs: [signalId], name: String(field.value) });
      patch.edges.push({
        id: stableEvidenceId('edge', `${artifactId}:CREATED_WITH:${toolId}`), source,
        from: artifactId, to: toolId, relation: 'CREATED_WITH', evidence_refs: [signalId],
      });
    }
  }
  patch.warnings.push({
    id: 'warning:editable-metadata',
    code: 'EDITABLE_METADATA',
    message_key: 'warnings.editable_metadata',
    evidence_refs: [signalId],
    source,
  });
  return patch;
}

export function enrichWithMetadata(
  original: VerificationResult,
  inspection: MetadataInspection,
): VerificationResult {
  const result = structuredClone(original);
  const c2paSignal = result.signals.find(({ kind }) => kind === 'C2PA_MANIFEST');

  if (inspection.error) {
    result.warnings.push({
      id: 'warning:metadata-parser-failure',
      code: 'METADATA_PARSER_FAILURE',
      message_key: 'warnings.metadata_parser_failure',
      evidence_refs: c2paSignal ? [c2paSignal.id] : [],
    });
    result.processing.errors.push({ code: 'METADATA_PARSER_FAILURE', adapter: 'MetadataAdapter' });
    result.processing.adapters.push({ name: 'MetadataAdapter', version: 'exifr@7.1.3' });
    if (result.overall_status === 'NO_SUPPORTED_PROVENANCE') {
      result.overall_status = 'INCONCLUSIVE';
      result.conclusions[0] = {
        id: 'conclusion:status',
        title_key: 'status.INCONCLUSIVE.title',
        explanation_key: 'status.INCONCLUSIVE.explanation',
        trace_refs: c2paSignal ? [c2paSignal.id, 'warning:metadata-parser-failure'] : ['warning:metadata-parser-failure'],
      };
    }
    return verificationResultSchema.parse(result);
  }

  result.processing.adapters.push({ name: 'MetadataAdapter', version: 'exifr@7.1.3' });
  if (inspection.fields.length === 0) {
    result.raw_provider_evidence = {
      ...result.raw_provider_evidence,
      metadata: { supported_fields_present: false, gps_read: false },
    };
    return verificationResultSchema.parse(result);
  }

  const signalId = stableEvidenceId('signal', `${result.artifact.sha256}:metadata:present`);
  result.signals.push({
    id: signalId,
    source: { adapter: 'MetadataAdapter', provider: 'Embedded image metadata' },
    evidence_refs: [],
    kind: 'EMBEDDED_METADATA',
    state: 'PRESENT',
    label: `${inspection.fields.length} supported metadata field${inspection.fields.length === 1 ? '' : 's'} found`,
    evidence_method: 'EMBEDDED_METADATA',
    evidence_strength: 'DECLARED',
    details: {
      families: [...new Set(inspection.fields.map(({ family }) => family))],
      cryptographically_verified: false,
    },
  });
  result.evidence_graph.edges.push({
    id: stableEvidenceId('edge', `${signalId}:${result.artifact.id}`),
    from: signalId,
    to: result.artifact.id,
    relation: 'DESCRIBES',
    evidence_refs: [],
  });

  const assertions: Assertion[] = inspection.fields.map((field, index) => ({
    id: stableEvidenceId('assertion', `${result.artifact.sha256}:metadata:${field.key}:${index}`),
    source: { adapter: 'MetadataAdapter', provider: field.family, native_id: field.key },
    evidence_refs: [signalId],
    label: field.label,
    value: field.value,
  }));
  result.assertions.push(...assertions);
  for (const assertion of assertions) {
    result.evidence_graph.edges.push({
      id: stableEvidenceId('edge', `${signalId}:CONTAINS:${assertion.id}`),
      from: signalId,
      to: assertion.id,
      relation: 'CONTAINS',
      evidence_refs: [],
    });
  }

  const tools: Tool[] = inspection.fields
    .filter(({ key }) => key === 'Software' || key === 'CreatorTool')
    .map((field, index) => ({
      id: stableEvidenceId('tool', `${result.artifact.sha256}:metadata-tool:${index}:${String(field.value)}`),
      source: { adapter: 'MetadataAdapter', provider: field.family, native_id: field.key },
      evidence_refs: [signalId],
      name: String(field.value),
    }));
  result.tools.push(...tools);
  for (const tool of tools) {
    result.evidence_graph.edges.push({
      id: stableEvidenceId('edge', `${result.artifact.id}:CREATED_WITH:${tool.id}`),
      from: result.artifact.id,
      to: tool.id,
      relation: 'CREATED_WITH',
      evidence_refs: [signalId],
    });
  }

  result.warnings.push({
    id: 'warning:editable-metadata',
    code: 'EDITABLE_METADATA',
    message_key: 'warnings.editable_metadata',
    evidence_refs: [signalId],
  });
  result.raw_provider_evidence = {
    ...result.raw_provider_evidence,
    metadata: {
      supported_fields_present: true,
      gps_read: false,
      fields: Object.fromEntries(inspection.fields.map(({ key, value }) => [key, value])),
    },
  };

  const provenanceBearingFields = inspection.fields.filter(({ family }) => family !== 'IMAGE');
  if (result.overall_status === 'NO_SUPPORTED_PROVENANCE' && provenanceBearingFields.length > 0) {
    result.overall_status = 'PROVENANCE_PRESENT';
    result.conclusions[0] = {
      id: 'conclusion:status',
      title_key: 'status.PROVENANCE_PRESENT.title',
      explanation_key: 'status.PROVENANCE_PRESENT.explanation',
      trace_refs: c2paSignal ? [c2paSignal.id, signalId] : [signalId],
    };
  }

  return verificationResultSchema.parse(result);
}

function primitiveValue(value: unknown): MetadataField['value'] | undefined {
  if (typeof value === 'string') {
    const clean = Array.from(value).filter((character) => {
      const code = character.codePointAt(0) ?? 0;
      return code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 127);
    }).join('').trim().slice(0, 500);
    return clean.length > 0 ? clean : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value;
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const safeBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const digest = await globalThis.crypto.subtle.digest('SHA-256', safeBuffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
